import type { FormModel } from './form';
import { IkeMap, type IMap } from '../ike.map';
import type { FormModelFieldState } from './form.field';
import { FormModelMetadata } from './form.metadata';

export class FormModelState {
	constructor(form: FormModel) {
		this._metadata = new FormModelMetadata(form);
	}

	frozen: boolean = false;

	private _id: string;

	public get id(): string {
		return this._id;
	}

	public set id(value: string) {
		this._id = value;
	}

	/**
	 * This object contains two kinds of state:
	 * 1. An entry for each field, where the key is `${this.id}:${fieldID}` and the value is FormModelFieldState.
	 * 2. An entry for the root subform, where the key is `:${this.id}` and the value is FormFieldSubFormState.
	 *
	 * Note that subform field states contains a list of fieldIds referencing the keys of the field states they own.
	 */
	fields: IMap<FormModelFieldState> = new IkeMap<FormModelFieldState>();

	private _revision: string;
	public get revision(): string {
		return this._revision;
	}
	public set revision(value: string) {
		this._revision = value;
	}

	private _version: string;

	/**
	 * @deprecated Mapsight-web form designer might still use this.
	 */
	public get version(): string {
		return this._version;
	}

	public set version(value: string) {
		this._version = value;
	}

	private _rootFormId: string;

	/**
	 * The first revision in the revision history of this form.
	 */
	public get rootFormId(): string {
		return this._rootFormId;
	}

	public set rootFormId(value: string) {
		this._rootFormId = value;
	}

	private _name: string = '';
	public get name(): string {
		return this._name;
	}
	public set name(value: string) {
		this._name = value;
	}

	private _displayName: string = '';
	public get displayName(): string {
		return this._displayName;
	}
	public set displayName(value: string) {
		this._displayName = value;
	}

	private _displayVersion: number = 0;
	public get displayVersion(): number {
		return this._displayVersion;
	}
	public set displayVersion(value: number) {
		this._displayVersion = value;
	}

	private _description: string = '';
	public get description(): string {
		return this._description;
	}
	public set description(value: string) {
		this._description = value;
	}

	private _updatedAt: Date = new Date();
	public get updatedAt(): Date {
		return this._updatedAt;
	}
	public set updatedAt(value: Date) {
		this._updatedAt = value;
	}

	private _createdAt: Date = new Date();
	public get createdAt(): Date {
		return this._createdAt;
	}
	public set createdAt(value: Date) {
		this._createdAt = value;
	}

	private _updatedBy: string = '';
	public get updatedBy(): string {
		return this._updatedBy;
	}
	public set updatedBy(value: string) {
		this._updatedBy = value;
	}

	private _v: number = 1;
	public get v(): number {
		return this._v;
	}
	public set v(value: number) {
		this._v = value;
	}

	private _metadata: FormModelMetadata;
	public get metadata(): FormModelMetadata {
		return this._metadata;
	}
	public set metadata(value: FormModelMetadata) {
		this._metadata = value;
	}

	setDisplayName(displayName: string) {
		this.displayName = displayName;
	}

	setDisplayVersion(displayVersion: number) {
		this.displayVersion = displayVersion;
	}

	setDescription(description: string) {
		this.description = description;
	}

	getFieldValue(fieldId: string): FormModelFieldState | undefined {
		return this.fields.get(fieldId);
	}

	setFieldValue<T extends FormModelFieldState>(fieldId: string, state: T) {
		if (this.frozen) {
			throw new Error(`Field ${fieldId}: Writing frozen state.`);
		}
		this.fields.set(fieldId, state);
	}

	removeField(fieldId: string) {
		this.fields.delete(fieldId);
	}
}
